import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Copy, Check, Clock, Database, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { resolveLabel } from '@/lib/fieldLabel';

/**
 * Read-only modal showing the executed query for a given visual: runtime +
 * engine badge, the MongoDB aggregation pipeline, and — for SQL datasources
 * (Snowflake, Postgres, …) — the native SQL that actually ran (with a
 * one-click copy button). Opened from the small "View executed query"
 * code-icon next to each visual's title.
 */
export function QueryViewDialog({ open, onOpenChange, visualId, queryInfo, allFields }) {
  const qi = visualId && queryInfo ? queryInfo[visualId] : null;
  const [copied, setCopied] = React.useState(false);

  // Query Details echoes the raw wire identifiers from the engine (e.g.
  // "ent_c978eded::FULL_LEGAL_NAME"). Resolve them to the friendly, role-aware
  // business name ("Insured · Full Legal Name") using the loaded model fields.
  const _fields = allFields || [];
  const _resolveMeasure = (m) => {
    if (typeof m !== 'string') return m;
    const mm = m.match(/^([A-Za-z0-9_]+)\((.*)\)$/);
    return mm ? `${mm[1]}(${resolveLabel(_fields, mm[2])})` : resolveLabel(_fields, m);
  };
  const dimLabels = (qi?.dimensions || []).map((d) => resolveLabel(_fields, d));
  const measureLabels = (qi?.measures || []).map(_resolveMeasure);
  const summaryText = (qi && (qi.dimensions || qi.measures))
    ? `${qi.error ? 'Attempted: ' : ''}Query groups by ${dimLabels.join(', ') || 'none'} and aggregates ${measureLabels.join(', ') || 'none'}`
    : qi?.summary;

  const copySql = async () => {
    if (!qi?.sql) return;
    try {
      await navigator.clipboard.writeText(qi.sql);
      setCopied(true);
      toast.success('SQL copied to clipboard');
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Could not copy — select and copy manually');
    }
  };

  // Friendly engine label: SQL sources show their dialect/name; otherwise MongoDB.
  const ranOnSql = !!qi?.sql;
  const engineLabel = ranOnSql
    ? (qi.source_name ? `${qi.source_name}${qi.dialect ? ` · ${String(qi.dialect).toUpperCase()}` : ''}` : (qi.dialect ? String(qi.dialect).toUpperCase() : 'SQL'))
    : 'MongoDB';
  const fellBack = qi?.executed_via === 'mongodb_fallback' && ranOnSql;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-2xl max-h-[80vh] overflow-auto">
        <DialogHeader><DialogTitle className="font-heading">Query Details</DialogTitle></DialogHeader>
        {qi ? (
          <div className="space-y-3 mt-2">
            {qi.error && (
              <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/30" data-testid="query-details-error">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-red-300 mb-0.5">Query could not run</p>
                    <p className="text-xs text-foreground/90 whitespace-pre-wrap">{String(qi.error).replace(/\b[A-Za-z0-9_]+::/g, '')}</p>
                  </div>
                </div>
              </div>
            )}
            {summaryText && <div className="p-3 rounded-xl bg-primary/5 border border-primary/10"><p className="text-xs text-foreground">{summaryText}</p></div>}

            {/* Runtime + engine badges */}
            {!qi.error && (
            <div className="flex items-center gap-2 flex-wrap" data-testid="query-runtime-badges">
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border border-border/50 bg-muted/20 text-[11px] text-foreground/85" data-testid="query-engine-badge">
                <Database className="w-3 h-3 text-primary" /> {engineLabel}
              </span>
              {typeof qi.duration_ms === 'number' && (
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border border-border/50 bg-muted/20 text-[11px] text-foreground/85" data-testid="query-runtime-badge">
                  <Clock className="w-3 h-3 text-primary" /> {qi.duration_ms} ms
                </span>
              )}
              {fellBack && (
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border border-amber-500/40 bg-amber-500/10 text-[11px] text-amber-200" data-testid="query-fallback-badge">
                  fell back to MongoDB
                </span>
              )}
              {qi.served_by_aggregate && (
                qi.aggregate_verified ? (
                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-[11px] text-emerald-300" data-testid="query-aggregate-badge" title={qi.aggregate_name || ''}>
                    <Database className="w-3 h-3" /> pre-aggregated{qi.aggregate_name ? ` · ${qi.aggregate_name}` : ''}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border border-amber-500/50 bg-amber-500/10 text-[11px] text-amber-300" data-testid="query-unverified-aggregate-badge" title={qi.aggregate_name || ''}>
                    <AlertTriangle className="w-3 h-3" /> unverified aggregate{qi.aggregate_name ? ` · ${qi.aggregate_name}` : ''}
                  </span>
                )
              )}
            </div>
            )}

            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Query Plan</p>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div><span className="text-muted-foreground">Dimensions:</span> <span className="text-foreground">{dimLabels.join(', ') || 'none'}</span></div>
                <div><span className="text-muted-foreground">Measures:</span> <span className="text-foreground">{measureLabels.join(', ') || 'none'}</span></div>
                <div><span className="text-muted-foreground">Filters:</span> <span className="font-mono text-foreground">{(qi.filters || []).map((f) => String(f).replace(/\b[A-Za-z0-9_]+::/g, '')).join(', ') || 'none'}</span></div>
                {!ranOnSql && !qi.error && <div><span className="text-muted-foreground">Pipeline Stages:</span> <span className="font-mono text-foreground">{qi.pipeline_stages}</span></div>}
              </div>
            </div>

            {qi.sql && (() => {
              const _stmtCount = String(qi.sql).split(/;\s*\n/).filter(s => s.trim()).length;
              return (
              <div data-testid="query-details-sql">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{engineLabel} SQL Query{_stmtCount > 1 ? ` · ${_stmtCount} sub-queries` : ''}</p>
                  <Button size="sm" variant="outline" onClick={copySql} data-testid="copy-sql-btn"
                    className="h-6 px-2 text-[10px] rounded-full border-primary/40 text-primary hover:bg-primary/10">
                    {copied ? <Check className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />}{copied ? 'Copied' : (_stmtCount > 1 ? 'Copy all' : 'Copy')}
                  </Button>
                </div>
                <pre className="p-3 rounded-xl bg-[#0d1117] border border-border/30 text-[10px] font-mono text-sky-300 overflow-auto max-h-64 whitespace-pre-wrap">{qi.sql}</pre>
              </div>
              );
            })()}

            {/* The MongoDB aggregation pipeline is only what actually runs for
                MongoDB-backed models. For SQL datasources (Snowflake, Postgres, …)
                the native SQL above is the source of truth, so we hide the
                Mongo pipeline to avoid showing a misleading representation. */}
            {!ranOnSql && !qi.error && (
              <div>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">MongoDB Aggregation Pipeline</p>
                <pre className="p-3 rounded-xl bg-[#0d1117] border border-border/30 text-[10px] font-mono text-emerald-300 overflow-auto max-h-64 whitespace-pre-wrap">{JSON.stringify(qi.pipeline, null, 2)}</pre>
              </div>
            )}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground text-center py-4">Run the visual's query first to see details</p>
        )}
      </DialogContent>
    </Dialog>
  );
}
